/**
 * CategoriesFilterPopover — v3.0.141
 *
 * Replaces the old fixed [All | Favorites | Archive] filter tabs on
 * Dashboard with a popover that shows built-ins + all user-created
 * categories with counts, plus a "Manage categories…" entry point.
 *
 * Props:
 *   activeCategoryId: string  — currently selected id ('all' | 'favorite' | 'archive' | 'cat_xxxx')
 *   onChange(id): fn          — called with new category id
 *   onManage(): fn            — opens the manage-categories modal
 *   refreshTrigger: any       — bump to force re-fetch (after CRUD)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ListFilter, Loader2, Settings, Check } from 'lucide-react';
import { Button } from './ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import { apiFetch } from '../App';

const COLOR_CLASSES = {
  slate:   'bg-slate-500/15 text-slate-200 border-slate-500/30',
  emerald: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
  sky:     'bg-sky-500/15 text-sky-200 border-sky-500/30',
  amber:   'bg-amber-500/15 text-amber-200 border-amber-500/30',
  rose:    'bg-rose-500/15 text-rose-200 border-rose-500/30',
  purple:  'bg-purple-500/15 text-purple-200 border-purple-500/30',
  orange:  'bg-orange-500/15 text-orange-200 border-orange-500/30',
  teal:    'bg-teal-500/15 text-teal-200 border-teal-500/30',
  pink:    'bg-pink-500/15 text-pink-200 border-pink-500/30',
  lime:    'bg-lime-500/15 text-lime-200 border-lime-500/30',
};

const dot = (color) => (
  <span
    aria-hidden="true"
    className={`inline-block w-2.5 h-2.5 rounded-full ${
      COLOR_CLASSES[color]?.split(' ')[0] || 'bg-slate-500/50'
    }`}
  />
);

export default function CategoriesFilterPopover({
  activeCategoryId = 'all', onChange, onManage, refreshTrigger,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [builtins, setBuiltins] = useState([]);
  const [custom, setCustom] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch('/categories');
      setBuiltins(Array.isArray(data?.builtins) ? data.builtins : []);
      setCustom(Array.isArray(data?.custom) ? data.custom : []);
    } catch (err) {
      toast.error(`Couldn't load categories: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshTrigger]);

  const all = useMemo(() => [...builtins, ...custom], [builtins, custom]);
  const active = all.find((c) => c.category_id === activeCategoryId)
    || builtins[0]
    || { name: 'All', emoji: '', color: 'slate' };

  const pick = (id) => {
    setOpen(false);
    if (onChange) onChange(id);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="border-white/20 bg-white/5 text-white hover:bg-white/10 gap-2"
          data-testid="categories-filter-btn"
        >
          <ListFilter className="h-4 w-4 shrink-0" />
          {active.emoji && <span className="text-base leading-none">{active.emoji}</span>}
          <span className="truncate max-w-[160px]">{active.name}</span>
          <span className="text-xs text-zinc-400 tabular-nums">
            ({active.song_count ?? 0})
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-1 bg-zinc-950 border-white/10 text-white"
        data-testid="categories-filter-popover"
      >
        {loading ? (
          <div className="py-6 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="max-h-[45vh] overflow-y-auto">
              {builtins.map((c) => (
                <FilterRow
                  key={c.category_id}
                  cat={c}
                  active={c.category_id === activeCategoryId}
                  onPick={pick}
                />
              ))}
              {custom.length > 0 && (
                <div className="h-px bg-white/10 mx-2 my-1" />
              )}
              {custom.map((c) => (
                <FilterRow
                  key={c.category_id}
                  cat={c}
                  active={c.category_id === activeCategoryId}
                  onPick={pick}
                />
              ))}
              {custom.length === 0 && (
                <div className="px-3 py-2 text-[11px] text-zinc-500">
                  Create your own categories to organize songs.
                </div>
              )}
            </div>
            <div className="h-px bg-white/10 my-1" />
            <button
              onClick={() => { setOpen(false); if (onManage) onManage(); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-white/5 rounded-sm"
              data-testid="categories-manage-btn"
            >
              <Settings className="h-4 w-4" />
              Manage categories…
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

function FilterRow({ cat, active, onPick }) {
  return (
    <button
      onClick={() => onPick(cat.category_id)}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm text-left transition-colors ${
        active ? 'bg-white/10 text-white' : 'text-zinc-300 hover:bg-white/5'
      }`}
      data-testid={`categories-filter-row-${cat.category_id}`}
    >
      <span className="w-4 flex justify-center text-base leading-none">
        {cat.emoji || dot(cat.color)}
      </span>
      <span className="flex-1 truncate">{cat.name}</span>
      <span className="text-xs text-zinc-500 tabular-nums">{cat.song_count ?? 0}</span>
      {active && <Check className="h-3.5 w-3.5 text-emerald-400" />}
    </button>
  );
}
